import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Link } from 'components/common';
import { Icon } from 'components/common/icons';

import type { NavLink } from 'types';

interface Props {
  activeLink: string;
  children?: React.ReactNode;
  links: NavLink[];
  preserveBackReference?: boolean;
}

const PageNav = ({
  activeLink,
  children,
  links,
  preserveBackReference = false
}: Props) => {
  const { theme } = useReduxState();

  return (
    <div className="container">
      <div className="nav">
        {children ? <div className="header">{children}</div> : null}

        <div className="links">
          {links.map((link, i) => (
            <div key={i} className={link.alignRight ? ' link-right' : ''}>
              <Link
                as={link.as}
                href={link.href}
                {...(preserveBackReference ? { replace: true } : {})}
              >
                <div
                  className={
                    'link' +
                    (link.as === activeLink || link.href === activeLink
                      ? ' active'
                      : '')
                  }
                >
                  <Icon
                    fixedWidth={true}
                    icon={link.icon}
                    style={{ fontSize: '1.2rem' }}
                  />
                  <span className="link-text">{link.title}</span>
                  {link.unreadItems ? (
                    <div className="unread-items">{link.unreadItems}</div>
                  ) : link.warningItems ? (
                    <div className="warning-items">{link.warningItems}</div>
                  ) : null}
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .nav {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          margin-top: 20px;
          padding: 0;
        }
        .header {
          border-bottom: 1px solid ${theme.lightGrey};
          padding: 15px;
        }
        .links {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding: 8px 10px;
          white-space: nowrap;
          width: 100%;
        }
        .link {
          align-items: center;
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColorLighter};
          display: inline-flex;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 1px;
          outline: 0;
          padding: 6px 10px 6px 8px;
          position: relative;
          text-decoration: none;
          transition: background-color 0.3s, color 0.3s;
        }
        .link:hover,
        .link:focus {
          background-color: ${theme.lightGreyLighter};
        }
        .link.active {
          background-color: ${theme.lightGrey};
          color: ${theme.textColor};
        }
        .link-right {
          margin-left: auto;
        }
        .link-text {
          display: none;
          margin-left: 4px;
        }
        .unread-items,
        .warning-items {
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 700;
          line-height: 1;
          padding: 2px 5px;
          position: absolute;
          right: -4px;
          top: -4px;
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          color: ${theme.dangerTextColor};
        }
        .warning-items {
          background-color: ${theme.warningColor};
          color: ${theme.warningTextColor};
        }
        @media (min-width: ${breakpoints.medium}px) {
          .info {
            flex-direction: row;
          }
          .link-text {
            display: inline;
          }
        }
      `}</style>
    </div>
  );
};

export default PageNav;
